/**
 * ADR-174 §5 — the Contract Number verb, client side. Pure helpers so the gate
 * and the messages are unit-testable outside JSX (like overlappingPositionLegs.js).
 *
 * The server is authoritative for every rule here; these only decide what the
 * UI offers and how a refusal reads.
 */
import { ACTIONS, SUBJECTS } from '../constants/permissions';
import { isRadarSourced, sourceSystemLabel } from '../constants/employmentSourceSystems';

/** Max length — mirrors radar-api CONTRACT_NUMBER_MAX_LENGTH (default 64). */
export const CONTRACT_NUMBER_MAX_LENGTH = 64;

const sourceRefOf = (episode) => {
  const ref = episode?.payload?.sourceReference;
  return ref && typeof ref === 'object' ? ref : null;
};

/** The stored number as text (Americas ids are JSON numbers — §5.5), or null. */
export const storedContractNumber = (episode) => {
  const value = sourceRefOf(episode)?.contractId;
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
};

/**
 * §5.2 / §5.3 — may this viewer set, correct or clear this contract's number?
 *   - only a radar-owned contract (no source, or 'radar'; owner decision Q1:
 *     Enova and Americas numbers are maintained by their source);
 *   - open: the annotation or the modification right;
 *   - closed: the ADR-168 history-correction right.
 *
 * @returns {{ allowed: boolean, reason: string|null }} reason explains a refusal the UI shows
 */
export const contractNumberGate = (ability, episode) => {
  if (!episode) return { allowed: false, reason: null };
  const ref = sourceRefOf(episode);
  if (!isRadarSourced(ref)) {
    return { allowed: false, reason: `Number maintained by ${sourceSystemLabel(ref?.source)}` };
  }
  const can = (subject) => Boolean(ability?.can?.(ACTIONS.UPDATE, subject));
  const allowed = episode.endDate
    ? can(SUBJECTS.EMPLOYMENT_EPISODE_HISTORY_CORRECTION)
    : (can(SUBJECTS.EMPLOYMENT_EPISODE_ANNOTATION) || can(SUBJECTS.EMPLOYMENT_EPISODE_MODIFICATION));
  return { allowed, reason: null };
};

/**
 * The value the API takes: trimmed text, or null to clear (§5.4 rule 3).
 * Never a Number — "0001021" keeps its zeros (§5.5).
 */
export const toContractNumberInput = (value) => {
  const text = value === undefined || value === null ? '' : String(value).trim();
  return text === '' ? null : text;
};

/** Client-side check before submit; the server re-checks. */
export const contractNumberInputProblem = (value) => {
  const text = toContractNumberInput(value);
  if (text && text.length > CONTRACT_NUMBER_MAX_LENGTH) {
    return `At most ${CONTRACT_NUMBER_MAX_LENGTH} characters`;
  }
  return null;
};

/**
 * A refusal, in words. CONTRACT_NUMBER_IN_USE names the other contract's
 * person only when the server judged the caller may see them.
 *
 * @param {Object} data - the error body ({ error, code, regionName, conflict })
 */
export const contractNumberRefusalMessage = (data) => {
  if (!data) return null;
  if (data.code === 'CONTRACT_NUMBER_IN_USE') {
    const where = data.regionName ? ` in ${data.regionName}` : '';
    const who = data.conflict?.fullName ? ` — it is on a contract of ${data.conflict.fullName}` : '';
    return `Contract number ${data.contractNumber || ''} is already used${where}${who}. Contract numbers are unique within a Region.`;
  }
  return data.error || null;
};
